import React, { useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const { get } = useApi();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Seed `error` from ?error=&message= on first render. Lazy initializer
  // runs before paint, so the error Alert shows on mount without a
  // cascading render. (react-hooks/set-state-in-effect would flag doing
  // this work in a useEffect.)
  const readErrorFromSearch = (params: URLSearchParams): string | null => {
    const errorType = params.get('error');
    const errorMessage = params.get('message');
    return errorType && errorMessage ? decodeURIComponent(errorMessage) : null;
  };
  const [error, setError] = React.useState<string | null>(() => readErrorFromSearch(searchParams));

  // Handle the edge case where the URL changes without the component
  // unmounting (e.g. a second OAuth callback redirect back into /login).
  // React docs endorse conditional setState during render when comparing
  // to previous prop/state values:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevSearch, setPrevSearch] = React.useState(searchParams.toString());
  const currentSearch = searchParams.toString();
  if (currentSearch !== prevSearch) {
    setPrevSearch(currentSearch);
    const next = readErrorFromSearch(searchParams);
    if (next !== null) setError(next);
  }

  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    // Strip the error params from the URL after they've been read into
    // state so a refresh doesn't re-show the error. This effect
    // intentionally does not call setError — state was already seeded from
    // the URL above.
    if (searchParams.has('error') || searchParams.has('message')) {
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      next.delete('message');
      navigate({ search: next.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleDiscordLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Store the intended destination in localStorage to survive the OAuth redirect
      const from = (location.state as any)?.from?.pathname;
      if (from) {
        localStorage.setItem('auth_redirect_path', from);
      }
      
      const response = await get('/auth/discord/url');
      window.location.href = response.url;
    } catch (err) {
      setError('Failed to initiate Discord login. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography component="h1" variant="h5" gutterBottom>
            Login to SagaBot
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Connect your Discord account to access SagaBot features
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleDiscordLogin}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ mt: 2 }}
          >
            {isLoading ? 'Connecting...' : 'Login with Discord'}
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 